import { useQuery } from '@tanstack/react-query';
import { FIB_SCAN_BARS, type Candle, type Swing, type Timeframe } from '@pipyar/core';
import { api } from '@/lib/api';

export interface FibCandlesResponse {
  symbol: string;
  timeframe: Timeframe;
  storedGmtOffsetSec: number;
  digits: number;
  count: number;
  candles: Candle[];
  live: Candle | null;
}

/** The candles the Fibonacci chart draws, for one timeframe. */
export function useFibCandles(symbol: string | null, timeframe: Timeframe) {
  const limit = FIB_SCAN_BARS[timeframe] ?? 1000;
  return useQuery({
    queryKey: ['fib-candles', symbol, timeframe, limit],
    queryFn: () =>
      api.get<FibCandlesResponse>(
        `/api/market/candles?symbol=${encodeURIComponent(symbol!)}&timeframe=${timeframe}` +
          `&limit=${limit}&includeLive=1`,
      ),
    enabled: Boolean(symbol),
    // A minute, not thirty seconds: the windows are deep (megabytes on M1)
    // and the last bar is moved live by useLiveBar between refetches anyway.
    staleTime: 45_000,
    refetchInterval: 60_000,
  });
}

/** A swing drawn by hand, with the reasoning behind it. */
export interface ManualSwing extends Swing {
  start: Swing['start'] & { label?: string };
  end: Swing['end'] & { label?: string };
  note?: string;
}

/**
 * As written in the file: the end may be `{ running: true }`, meaning the
 * move is still in progress and the end is the extreme reached since the
 * start — recomputed from the candles rather than typed in by hand.
 */
export interface ManualSwingEntry extends Omit<ManualSwing, 'end'> {
  end: ManualSwing['end'] | { running: true; label?: string };
}

/** Resolve a running end against the timeframe's bars; null if no bar follows the start. */
export function resolveSwing(entry: ManualSwingEntry, bars: readonly Candle[]): ManualSwing | null {
  if (!('running' in entry.end)) return entry as ManualSwing;
  let best: Candle | null = null;
  for (const bar of bars) {
    if (bar.time <= entry.start.time) continue;
    if (
      best === null ||
      (entry.direction === 'up' ? bar.high > best.high : bar.low < best.low)
    ) {
      best = bar;
    }
  }
  if (!best) return null;
  return {
    ...entry,
    end: {
      time: best.time,
      // A running end is the extreme itself: nothing has confirmed yet.
      price: entry.direction === 'up' ? best.high : best.low,
      label: entry.end.label ?? 'running',
    },
  };
}

type ManualSwings = Record<string, Record<string, ManualSwingEntry>>;

/**
 * The hand-drawn swings, one per symbol and timeframe.
 *
 * A static file in the document root rather than an API: nothing computes
 * these yet. While the selection rules are being worked out the swing on each
 * chart is chosen by eye and written down here, so the chart shows a specific
 * draw that can be argued with rather than an algorithm's guess.
 */
export function useManualSwings() {
  return useQuery({
    queryKey: ['fib-manual'],
    queryFn: async () => {
      const res = await fetch(`/fib/manual.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`manual.json: HTTP ${res.status}`);
      const body = (await res.json()) as ManualSwings & { _comment?: string };
      delete body._comment;
      return body as ManualSwings;
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Teaching draws: Fibonaccis the owner places on the chart by hand.
// ---------------------------------------------------------------------------

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TeachingFib, TeachingFibInput, TeachingFibPatch } from '@pipyar/core';

export function useTeachingFibs(symbol: string | null) {
  return useQuery({
    queryKey: ['fib-teaching', symbol],
    queryFn: () =>
      api.get<{ fibs: TeachingFib[] }>(`/api/fib/teaching?symbol=${encodeURIComponent(symbol!)}`),
    enabled: Boolean(symbol),
    staleTime: 30_000,
  });
}

export function useTeachingFibMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['fib-teaching'] });

  const create = useMutation({
    mutationFn: (input: TeachingFibInput) =>
      api.post<{ fib: TeachingFib }>('/api/fib/teaching', input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: TeachingFibPatch }) =>
      api.patch<{ fib: TeachingFib }>(`/api/fib/teaching/${id}`, patch),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del<{ ok: true }>(`/api/fib/teaching/${id}`),
    onSuccess: invalidate,
  });
  return { create, update, remove };
}

// ---------------------------------------------------------------------------
// Auto swings: what the rules draw right now, computed by the API.
// ---------------------------------------------------------------------------

import type { AutoSwing } from '@pipyar/core';

export interface AutoSwingsResponse {
  symbol: string;
  digits: number;
  storedGmtOffsetSec: number;
  swings: Record<string, AutoSwing | null>;
}

export function useAutoSwings(symbol: string | null) {
  return useQuery({
    queryKey: ['fib-auto', symbol],
    queryFn: () => api.get<AutoSwingsResponse>(`/api/fib/auto?symbol=${encodeURIComponent(symbol!)}`),
    enabled: Boolean(symbol),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
